import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from "@stripe/react-stripe-js";
import { Check, Sparkles, Zap, Crown, Rocket } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { getStripe, getStripeEnvironment } from "@/lib/stripe";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PaymentTestModeBanner } from "@/components/PaymentTestModeBanner";
import { toast } from "sonner";

type Tier = {
  key: "essential" | "growth" | "dominate" | "titan";
  name: string;
  monthly: number;
  deposit: number;
  tagline: string;
  features: string[];
  icon: React.ComponentType<{ className?: string }>;
  highlighted?: boolean;
};

const TIERS: Tier[] = [
  {
    key: "essential",
    name: "Essential",
    monthly: 197,
    deposit: 500,
    tagline: "Start automating the basics.",
    icon: Sparkles,
    features: [
      "AI voice agent (inbound)",
      "Basic CRM automation",
      "Lead capture chatbot",
      "Email support",
    ],
  },
  {
    key: "growth",
    name: "Growth",
    monthly: 497,
    deposit: 1000,
    tagline: "Scale operations without scaling staff.",
    icon: Zap,
    highlighted: true,
    features: [
      "Everything in Essential",
      "AI voice + text agents",
      "Advanced CRM workflows",
      "Lead routing & analytics",
      "Priority support",
    ],
  },
  {
    key: "dominate",
    name: "Dominate",
    monthly: 897,
    deposit: 1500,
    tagline: "Own your market with full AI infrastructure.",
    icon: Crown,
    features: [
      "Everything in Growth",
      "Custom AI workflows",
      "Dedicated success manager",
      "Multi-channel automation",
      "Advanced reporting dashboards",
    ],
  },
  {
    key: "titan",
    name: "Titan",
    monthly: 1200,
    deposit: 1500,
    tagline: "Enterprise-grade AI workforce.",
    icon: Rocket,
    features: [
      "Everything in Dominate",
      "Custom AI workforce build-out",
      "Multi-location deployment",
      "White-glove onboarding",
      "24/7 dedicated support",
    ],
  },
];

const Pricing = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [openTier, setOpenTier] = useState<Tier | null>(null);

  useEffect(() => {
    document.title = "AI Service Pricing | Neil AI Global Solutions";
  }, []);

  const fetchClientSecret = useMemo(() => {
    if (!openTier || !user) return null;
    return async () => {
      const { data, error } = await supabase.functions.invoke("create-ai-subscription-checkout", {
        body: {
          environment: getStripeEnvironment(),
          tier: openTier.key,
          returnUrl: `${window.location.origin}/portal?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
        },
      });
      if (error || !data?.clientSecret) {
        const msg = error?.message || data?.error || "Failed to start checkout";
        toast.error(msg);
        throw new Error(msg);
      }
      return data.clientSecret as string;
    };
  }, [openTier, user]);

  const handleSubscribe = (tier: Tier) => {
    if (!user) {
      toast.info("Please sign in to subscribe.");
      navigate(`/auth?redirect=${encodeURIComponent("/pricing")}`);
      return;
    }
    setOpenTier(tier);
  };

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <PaymentTestModeBanner />

      <div className="container mx-auto px-4">
        <header className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-primary via-blue-400 to-yellow-400 bg-clip-text text-transparent">
            AI Service Pricing
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Choose the AI infrastructure that fits your business. Every plan includes a one-time setup deposit and a recurring monthly subscription — billed automatically through your first invoice.
          </p>
          {!loading && !user && (
            <p className="text-sm text-muted-foreground mt-4">
              You'll be asked to sign in before checkout so we can link the subscription to your account.
            </p>
          )}
        </header>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 max-w-7xl mx-auto">
          {TIERS.map((tier) => {
            const Icon = tier.icon;
            return (
              <Card
                key={tier.key}
                className={`relative p-6 flex flex-col bg-card/60 backdrop-blur border ${
                  tier.highlighted
                    ? "border-primary shadow-[0_0_40px_-10px_hsl(var(--primary)/0.5)]"
                    : "border-border"
                }`}
              >
                {tier.highlighted && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-semibold rounded-full bg-primary text-primary-foreground">
                    Most Popular
                  </span>
                )}
                <Icon className="w-8 h-8 mb-3 text-primary" />
                <h2 className="text-2xl font-bold">{tier.name}</h2>
                <p className="text-sm text-muted-foreground mb-4">{tier.tagline}</p>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold">${tier.monthly.toLocaleString()}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    + ${tier.deposit.toLocaleString()} one-time setup
                  </p>
                </div>

                <ul className="space-y-2 mb-6 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className="w-full"
                  variant={tier.highlighted ? "default" : "outline"}
                  onClick={() => handleSubscribe(tier)}
                >
                  Get Started
                </Button>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-10 max-w-2xl mx-auto">
          Setup deposits are billed on your first invoice alongside your first month. Subscriptions renew monthly and can be canceled anytime from your customer portal. All prices in USD; applicable taxes calculated at checkout.
        </p>
      </div>

      <Dialog open={!!openTier} onOpenChange={(o) => !o && setOpenTier(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Subscribe to Neil AI {openTier?.name} — ${openTier?.monthly}/mo + ${openTier?.deposit} setup
            </DialogTitle>
          </DialogHeader>
          {openTier && fetchClientSecret && (
            <div id="checkout" key={openTier.key}>
              <EmbeddedCheckoutProvider
                stripe={getStripe()}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Pricing;
